import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api, errorMessage } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { Callout } from '@/ui/bits';
import { InputField } from '@/ui/Field';
import { GoogleMark, Icon } from '@/ui/Icon';
import { AuthBrand, AuthFrame } from './AuthFrame';

// Sign in: Google when the team has set it up, or the email and password from the invitation.

export interface AuthOptions {
  workspaceName: string;
  google: boolean;
}

export const authOptionsQuery = {
  queryKey: ['auth-options'],
  queryFn: () => api.get<AuthOptions>('/auth/options'),
  staleTime: 5 * 60_000,
} as const;

export function Login({ notice }: { /** A message to show above the form, such as why Google sign-in failed. */ notice: string | null }) {
  const { data: options } = useQuery(authOptionsQuery);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(notice);
  const [fields, setFields] = useState<Record<string, string>>({});

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const local: Record<string, string> = {};
    if (!email.trim()) local.email = 'Enter your email';
    if (!password) local.password = 'Enter your password';
    setFields(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.post('/auth/login', { email: email.trim(), password });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) setFields(err.fields);
      else setProblem(errorMessage(err));
      if (err instanceof ApiError && err.status === 401) setPassword('');
      setBusy(false);
    }
  };

  return (
    <AuthFrame label="Sign in to Clientbook">
      <div className="auth-card">
        <AuthBrand workspaceName={options?.workspaceName} />
        <h1 className="auth-h">Sign in</h1>
        <p className="auth-sub">
          {options ? `Welcome back to ${options.workspaceName}'s client book.` : 'Welcome back to your team’s client book.'}
        </p>
        <div className="auth-body">
          {problem ? (
            <Callout tone="danger" role="alert">
              {problem}
            </Callout>
          ) : null}
          {options?.google ? (
            <>
              <a className="btn btn-lg auth-google" href="/auth/google/start?purpose=signin">
                <GoogleMark />
                Continue with Google
              </a>
              <div className="auth-or">or</div>
            </>
          ) : null}
          <form className="auth-form" onSubmit={submit} noValidate>
            <InputField
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              value={email}
              error={fields.email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <InputField
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              error={fields.password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" className="btn btn-primary btn-lg" disabled={busy} aria-busy={busy || undefined}>
              {busy ? 'Signing in' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
      <p className="auth-foot">
        <Icon name="key" />
        Forgot your password? Your super admin can send you a link to choose a new one.
      </p>
    </AuthFrame>
  );
}
